import { getSupabaseBrowserClient } from '../lib/supabase'
import { getOrganisationalAiSolution } from './solution-api'

export type OperationalState = 'proposed' | 'experiment' | 'pilot' | 'production' | 'suspended' | 'retired'
export type AttentionOutcome = 'attention_needed' | 'information_or_specialist_review_needed' | 'no_immediate_attention_signal' | 'not_screened'

export type ProductLibraryResult = {
  templateId: string
  templateName: string
  templateVersionId: string
  templateVerificationLabel: 'verified_supplier_information' | 'swell_interpretation' | 'customer_confirmation_required'
  lastReviewedDate: string | null
  productId: string | null
  productName: string | null
  supplierId: string | null
  supplierName: string | null
  offeringId: string | null
  offeringName: string | null
  categoryNames: string[]
  summary: string | null
  commonBusinessPurpose: string | null
}

export type ProductLibraryTemplate = {
  templateId: string
  templateName: string
  templateVersionId: string
  templateVersionNo: number
  verificationLabel: string
  availabilityStatus: string
  applicableRegions: string[]
  licenceQualifier: string | null
  effectiveDate: string | null
  lastReviewedDate: string | null
  content: Record<string, unknown>
  products: { id: string; name: string; supplierId: string | null; supplierName: string | null; catalogueVersionId: string | null }[]
  offerings: { id: string; name: string; supplierId: string | null; catalogueVersionId: string | null }[]
  capabilities: { id: string; name: string; catalogueVersionId: string | null }[]
  sources: { title: string; url: string; sourceType: string; publisher: string; applicableRegion: string | null; editionOrLicence: string | null; effectiveDate: string | null; lastReviewedDate: string | null }[]
}

export type ClassificationOption = {
  valueCode: string
  label: string
  helpText: string | null
  exampleText: string | null
  displayOrder: number
}

export type EntryOptions = {
  businessUnits: { id: string; name: string }[]
  members: { membershipId: string; displayName: string }[]
  objectives: { id: string; title: string }[]
  initiatives: { id: string; name: string; stage: string }[]
  classificationSets: Record<string, ClassificationOption[]>
  canManageRegister: boolean
  canReadPortfolio: boolean
  currentMembershipId: string | null
}

export type OrganisationSolution = {
  id: string
  name: string
  businessOwnerMembershipId: string | null
  technicalOwnerMembershipId: string | null
  operatingEnvironment: string | null
  intendedAudience: string[]
  containsMultipleAgents: boolean | null
  technicalDocumentationUrl: string | null
  productId: string | null
  productName: string | null
  privateProductId: string | null
  supplierName: string | null
  offeringId: string | null
  offeringName: string | null
  useCaseCount: number
}

export type AiUseDraft = {
  id: string
  organisationId: string
  status: 'draft' | 'converted' | 'abandoned'
  entryChoice: 'product_library' | 'product_not_listed' | 'product_unknown'
  currentStep: number
  draftData: Record<string, unknown>
  templateId: string | null
  templateVersionId: string | null
  catalogueSnapshot: ProductLibraryTemplate | null
  suggestedFields: Record<string, unknown>
  confirmedFields: string[]
  convertedUseCaseId: string | null
  updatedAt: string
}

export type DraftListItem = {
  id: string
  status: string
  entryChoice: string
  currentStep: number
  name: string
  updatedAt: string
}

export type ScreeningFindingPreview = {
  questionCode: string
  question: string
  answer: 'yes' | 'needs_confirmation'
  ruleCode: string
  ruleVersionId: string
  title: string
  implication: string
  recommendedReviewer: string | null
  recommendedNextStep: string | null
}

export type ScreeningPreview = {
  outcomeCode: AttentionOutcome
  outcomeLabel: string
  findings: ScreeningFindingPreview[]
}

export type RegisterEntry = {
  id: string
  title: string
  businessPurpose: string
  businessUnitId: string | null
  businessUnitName: string | null
  businessOwnerMembershipId: string | null
  businessOwnerName: string | null
  operationalState: OperationalState
  solutionId: string | null
  solutionName: string | null
  productName: string | null
  supplierName: string | null
  primaryActivity: string | null
  primaryActivityLabel: string | null
  strategyAlignment: 'aligned' | 'not_yet_aligned'
  primaryObjective: string | null
  governanceAttention: AttentionOutcome
  lastMaterialUpdateAt: string
  ownerOrInformationMissing: boolean
  summaryNeedsAttention: boolean
  summaryOwnerOrInformationMissing: boolean
}

export type RegisterSummary = {
  total: number
  liveOrPilot: number
  needsAttention: number
  ownerOrInformationMissing: number
}

export type AiUseDecisionTrace = Record<string, unknown>

export type AiUseDetail = Record<string, unknown> & {
  id: string
  organisationId: string
  title: string
  businessPurpose: string
  operationalState: OperationalState
  businessUnitName: string | null
  businessOwnerName: string | null
  technicalOwnerName: string | null
  expectedOutcome: string | null
  benefitExplanation: string | null
  alignmentExplanation: string | null
  intendedSuccessMeasure: string | null
  lastMaterialUpdateAt: string
  source: string
  solution: Record<string, unknown> | null
  classifications: Record<string, unknown>[]
  objectives: Record<string, unknown>[]
  initiatives: Record<string, unknown>[]
  catalogueContext: Record<string, unknown> | null
  screening: Record<string, unknown> | null
  catalogueReviewRequired: boolean
  history: Record<string, unknown>[]
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

async function invoke(functionName: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

function object(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {} }
function rows(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.map(object) : [] }
function str(value: unknown): string { return typeof value === 'string' ? value : '' }
function maybe(value: unknown): string | null { return typeof value === 'string' && value.length > 0 ? value : null }
function num(value: unknown): number { return typeof value === 'number' ? value : Number(value) || 0 }
function bool(value: unknown): boolean { return value === true || value === 'true' }
function strings(value: unknown): string[] { return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [] }

export async function searchProductLibrary(organisationId: string, query: string): Promise<ProductLibraryResult[]> {
  return rows(await invoke('search_ai_product_library', { p_organisation_id: organisationId, p_query: query })).map((row) => ({
    templateId: str(row.templateId), templateName: str(row.templateName), templateVersionId: str(row.templateVersionId),
    templateVerificationLabel: str(row.templateVerificationLabel) as ProductLibraryResult['templateVerificationLabel'],
    lastReviewedDate: maybe(row.lastReviewedDate), productId: maybe(row.productId), productName: maybe(row.productName), supplierId: maybe(row.supplierId), supplierName: maybe(row.supplierName),
    offeringId: maybe(row.offeringId), offeringName: maybe(row.offeringName), categoryNames: strings(row.categoryNames), summary: maybe(row.summary), commonBusinessPurpose: maybe(row.commonBusinessPurpose),
  }))
}

export async function getProductLibraryTemplate(organisationId: string, templateId: string): Promise<ProductLibraryTemplate> {
  const row = object(await invoke('get_ai_product_library_template', { p_organisation_id: organisationId, p_template_id: templateId }))
  return {
    templateId: str(row.templateId), templateName: str(row.templateName), templateVersionId: str(row.templateVersionId), templateVersionNo: num(row.templateVersionNo),
    verificationLabel: str(row.verificationLabel), availabilityStatus: str(row.availabilityStatus), applicableRegions: strings(row.applicableRegions), licenceQualifier: maybe(row.licenceQualifier), effectiveDate: maybe(row.effectiveDate), lastReviewedDate: maybe(row.lastReviewedDate), content: object(row.content),
    products: rows(row.products).map((item) => ({ id: str(item.id), name: str(item.name), supplierId: maybe(item.supplierId), supplierName: maybe(item.supplierName), catalogueVersionId: maybe(item.catalogueVersionId) })),
    offerings: rows(row.offerings).map((item) => ({ id: str(item.id), name: str(item.name), supplierId: maybe(item.supplierId), catalogueVersionId: maybe(item.catalogueVersionId) })),
    capabilities: rows(row.capabilities).map((item) => ({ id: str(item.id), name: str(item.name), catalogueVersionId: maybe(item.catalogueVersionId) })),
    sources: rows(row.sources).map((item) => ({ title: str(item.title), url: str(item.url), sourceType: str(item.sourceType), publisher: str(item.publisher), applicableRegion: maybe(item.applicableRegion), editionOrLicence: maybe(item.editionOrLicence), effectiveDate: maybe(item.effectiveDate), lastReviewedDate: maybe(item.lastReviewedDate) })),
  }
}

export async function createAiUseDraft(organisationId: string, entryChoice: AiUseDraft['entryChoice'], templateId?: string | null): Promise<string> {
  return str(await invoke('create_ai_use_draft', { p_organisation_id: organisationId, p_entry_choice: entryChoice, p_template_id: templateId || null }))
}
export async function getAiUseDraft(draftId: string): Promise<AiUseDraft> {
  const row = object(await invoke('get_ai_use_draft', { p_draft_id: draftId }))
  return { id: str(row.id), organisationId: str(row.organisationId), status: str(row.status) as AiUseDraft['status'], entryChoice: str(row.entryChoice) as AiUseDraft['entryChoice'], currentStep: num(row.currentStep), draftData: object(row.draftData), templateId: maybe(row.templateId), templateVersionId: maybe(row.templateVersionId), catalogueSnapshot: row.catalogueSnapshot ? object(row.catalogueSnapshot) as unknown as ProductLibraryTemplate : null, suggestedFields: object(row.suggestedFields), confirmedFields: strings(row.confirmedFields), convertedUseCaseId: maybe(row.convertedUseCaseId), updatedAt: str(row.updatedAt) }
}
export async function listMyAiUseDrafts(organisationId: string): Promise<DraftListItem[]> {
  return rows(await invoke('list_my_ai_use_drafts', { p_organisation_id: organisationId })).map((row) => ({ id: str(row.id), status: str(row.status), entryChoice: str(row.entryChoice), currentStep: num(row.currentStep), name: str(row.name), updatedAt: str(row.updatedAt) }))
}
export async function saveAiUseDraft(draftId: string, currentStep: number, draftData: Record<string, unknown>, confirmedFields: string[] = []): Promise<void> {
  await invoke('save_ai_use_draft', { p_draft_id: draftId, p_current_step: currentStep, p_draft_data: draftData, p_confirmed_fields: confirmedFields })
}
export async function abandonAiUseDraft(draftId: string): Promise<void> { await invoke('abandon_ai_use_draft', { p_draft_id: draftId }) }
export async function createPrivateProductForDraft(draftId: string, supplierName: string, productName: string, description = ''): Promise<string> {
  return str(await invoke('create_private_product_for_draft', { p_draft_id: draftId, p_supplier_name: supplierName || null, p_product_name: productName, p_description: description || null }))
}
export async function getAiUseEntryOptions(organisationId: string): Promise<EntryOptions> {
  const row = object(await invoke('get_ai_use_entry_options', { p_organisation_id: organisationId }))
  const classificationSets: Record<string, ClassificationOption[]> = {}
  for (const [key, value] of Object.entries(object(row.classificationSets))) classificationSets[key] = rows(value).map((item) => ({ valueCode: str(item.valueCode), label: str(item.label), helpText: maybe(item.helpText), exampleText: maybe(item.exampleText), displayOrder: num(item.displayOrder) }))
  return {
    businessUnits: rows(row.businessUnits).map((item) => ({ id: str(item.id), name: str(item.name) })), members: rows(row.members).map((item) => ({ membershipId: str(item.membershipId), displayName: str(item.displayName) })),
    objectives: rows(row.objectives).map((item) => ({ id: str(item.id), title: str(item.title) })), initiatives: rows(row.initiatives).map((item) => ({ id: str(item.id), name: str(item.name), stage: str(item.stage) })), classificationSets,
    canManageRegister: bool(row.canManageRegister), canReadPortfolio: bool(row.canReadPortfolio), currentMembershipId: maybe(row.currentMembershipId),
  }
}
export async function listOrganisationAiSolutions(organisationId: string): Promise<OrganisationSolution[]> {
  return rows(await invoke('list_organisation_ai_solutions', { p_organisation_id: organisationId })).map((row) => ({ id: str(row.id), name: str(row.name), businessOwnerMembershipId: maybe(row.businessOwnerMembershipId), technicalOwnerMembershipId: maybe(row.technicalOwnerMembershipId), operatingEnvironment: maybe(row.operatingEnvironment), intendedAudience: strings(row.intendedAudience), containsMultipleAgents: typeof row.containsMultipleAgents === 'boolean' ? row.containsMultipleAgents : null, technicalDocumentationUrl: maybe(row.technicalDocumentationUrl), productId: maybe(row.productId), productName: maybe(row.productName), privateProductId: maybe(row.privateProductId), supplierName: maybe(row.supplierName), offeringId: maybe(row.offeringId), offeringName: maybe(row.offeringName), useCaseCount: num(row.useCaseCount) }))
}
export async function previewAiUseScreening(organisationId: string, answers: Record<string, string>): Promise<ScreeningPreview> {
  const row = object(await invoke('preview_ai_use_screening', { p_organisation_id: organisationId, p_answers: answers }))
  return { outcomeCode: str(row.outcomeCode) as AttentionOutcome, outcomeLabel: str(row.outcomeLabel), findings: rows(row.findings).map((item) => ({ questionCode: str(item.questionCode), question: str(item.question), answer: str(item.answer) as ScreeningFindingPreview['answer'], ruleCode: str(item.ruleCode), ruleVersionId: str(item.ruleVersionId), title: str(item.title), implication: str(item.implication), recommendedReviewer: maybe(item.recommendedReviewer), recommendedNextStep: maybe(item.recommendedNextStep) })) }
}
export async function finaliseAiUseDraft(draftId: string): Promise<string> { return str(await invoke('finalise_ai_use_draft', { p_draft_id: draftId })) }
export async function createAnotherAiUseDraft(useCaseId: string): Promise<string> { return str(await invoke('create_another_ai_use_draft', { p_source_use_case_id: useCaseId })) }

export async function getRegisterSummary(organisationId: string): Promise<RegisterSummary> {
  const row = object(await invoke('get_ai_register_summary', { p_organisation_id: organisationId }))
  return { total: num(row.total), liveOrPilot: num(row.liveOrPilot), needsAttention: num(row.needsAttention), ownerOrInformationMissing: num(row.ownerOrInformationMissing) }
}
export async function listRegisterEntries(input: { organisationId: string; search?: string; businessUnitId?: string; operationalState?: string; primaryActivity?: string; ownerMembershipId?: string; alignment?: string; attention?: string }): Promise<RegisterEntry[]> {
  return rows(await invoke('list_ai_register_entries', {
    p_organisation_id: input.organisationId, p_search: input.search || null, p_business_unit_id: input.businessUnitId || null, p_operational_state: input.operationalState || null,
    p_primary_activity: input.primaryActivity || null, p_owner_membership_id: input.ownerMembershipId || null, p_alignment: input.alignment || null, p_attention: input.attention || null,
  })).map((row) => ({
    id: str(row.id), title: str(row.title), businessPurpose: str(row.businessPurpose), businessUnitId: maybe(row.businessUnitId), businessUnitName: maybe(row.businessUnitName), businessOwnerMembershipId: maybe(row.businessOwnerMembershipId), businessOwnerName: maybe(row.businessOwnerName), operationalState: str(row.operationalState) as OperationalState,
    solutionId: maybe(row.solutionId), solutionName: maybe(row.solutionName), productName: maybe(row.productName), supplierName: maybe(row.supplierName), primaryActivity: maybe(row.primaryActivity), primaryActivityLabel: maybe(row.primaryActivityLabel), strategyAlignment: str(row.strategyAlignment) as RegisterEntry['strategyAlignment'], primaryObjective: maybe(row.primaryObjective), governanceAttention: str(row.governanceAttention) as AttentionOutcome, lastMaterialUpdateAt: str(row.lastMaterialUpdateAt), ownerOrInformationMissing: bool(row.ownerOrInformationMissing), summaryNeedsAttention: bool(row.summaryNeedsAttention), summaryOwnerOrInformationMissing: bool(row.summaryOwnerOrInformationMissing),
  }))
}

function adaptSolutionDetailForRegister(solution: Record<string, unknown>): Record<string, unknown> {
  const productContext = object(solution.productContext)
  return {
    ...solution,
    productId: maybe(productContext.productId),
    productName: maybe(productContext.productName),
    privateProductId: maybe(productContext.privateProductId),
    supplierName: maybe(productContext.supplierName),
    offeringId: maybe(productContext.offeringId),
    offeringName: maybe(productContext.offeringName),
  }
}

async function getLinkedSolutionForUseCase(useCaseId: string): Promise<Record<string, unknown> | null> {
  const client = getSupabaseBrowserClient()
  const { data, error } = await client
    .from('ai_use_cases')
    .select('organisational_solution_id')
    .eq('id', useCaseId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  const solutionId = maybe(object(data).organisational_solution_id)
  if (!solutionId) return null
  return adaptSolutionDetailForRegister(await getOrganisationalAiSolution(solutionId))
}

export async function getRegisterEntry(useCaseId: string): Promise<AiUseDetail> {
  const detail = object(await invoke('get_ai_register_entry', { p_ai_use_case_id: useCaseId })) as AiUseDetail
  if (detail.solution) return detail

  const solution = await getLinkedSolutionForUseCase(useCaseId)
  return solution ? { ...detail, solution } : detail
}

export async function getAiUseDecisionTrace(useCaseId: string): Promise<AiUseDecisionTrace> {
  return object(await invoke('get_ai_use_decision_trace', { p_ai_use_case_id: useCaseId }))
}
